// The multi-industry contract. This file is the single source of truth that
// apps/api implements and apps/web imports for its props and fetch results.
// It imports nothing, so either side can read it without pulling in a runtime.
//
// One tenant per industry dataset (apps/api/data/industries/*.json.gz). The web
// app picks the tenant from its subdomain (financial.localhost:3000) and calls
// /api/i/:slug/... ; bare localhost opens DEFAULT_INDUSTRY.
//
// The 13 industry definitions come from docs/metrics/metrics-map-13-industri.txt
// (the "Metrics Map 13 Industri" Google Doc). The 14th tenant, pmb, is the
// owner's admissions dataset (300 registrants, periode 2025 Ganjil); the doc has
// no section for it, so its stages and rules are written from that data and say
// so. Every value is computed from the dataset; nothing on the wire is typed in
// by hand.

export const INDUSTRY_SLUGS = [
  "financial",
  "agency",
  "fmcg",
  "fashion-beauty",
  "telco",
  "entertainment",
  "hospitality",
  "politics-gov-ngo",
  "automotive-transport",
  "logistic",
  "home-appliance",
  "food-beverage",
  "health",
  // The owner's admissions (PMB) data, kept as its own tenant at
  // pmb.localhost:3000 (owner's call, 2026-10-01).
  "pmb",
] as const;

export type IndustrySlug = (typeof INDUSTRY_SLUGS)[number];

/** What bare localhost (no subdomain) opens. Owner's call, 2026-10-01. */
export const DEFAULT_INDUSTRY: IndustrySlug = "financial";

/** The four base patterns the doc groups industries into, plus impact for politics/NGO. */
export type IndustryPattern = "distribusi" | "langganan" | "kapasitas" | "funnel" | "dampak";

export interface IndustrySummary {
  slug: IndustrySlug;
  /** Full name from the dataset, e.g. "Financial (Insurance, Banking, Fintech)". */
  name: string;
  /** Short name for a header, e.g. "Financial". */
  shortName: string;
  pattern: IndustryPattern;
  /** ISO dates of the dataset period. */
  periodStart: string;
  periodEnd: string;
  currency: string;
  /** The dataset's own note. Today every file says it is synthetic; the UI shows it. */
  note: string;
  /** The North Star in one sentence, from the doc. */
  northStar: string;
}

/**
 * How a value renders. "percent" values are 0 to 100 (the scale fact_target
 * uses), never 0 to 1. "idr" is rupiah, "score" is a rating on its own scale.
 */
export type MetricFormat = "idr" | "count" | "percent" | "decimal" | "minutes" | "hours" | "days" | "score";

/** Which way is better for this metric. */
export type Better = "up" | "down";

export type Verdict = "good" | "watch" | "attention";

/** A metric graded against fact_target. Only present when the target is comparable. */
export interface TargetStatus {
  target: number;
  warning: number;
  critical: number;
  verdict: Verdict;
  /** The rule in words, e.g. "Target 49,9%; waspada di bawah 44,9%; kritis di bawah 39,9% (rata-rata target bulanan semua wilayah)". */
  basis: string;
}

/** One computed metric. */
export interface MetricValue {
  id: string;
  label: string;
  /** Null when the data cannot produce it; `note` then says why. */
  value: number | null;
  format: MetricFormat;
  better: Better;
  /** The process stage it belongs to (Stage.id). */
  stageId: string;
  /** How it is computed, in words, naming the tables and columns. */
  formula: string;
  /** Tables it reads, e.g. ["fact_application"]. */
  sources: string[];
  /** Monthly values in time order, when the metric has a time grain. */
  trend: { period: string; label: string; value: number }[];
  /** Graded against fact_target, or null when there is no comparable target. */
  target: TargetStatus | null;
  /** Why a value is null, or why a target was not graded. Plain sentences, no em-dash. */
  note?: string;
}

/** One row of the doc's stage table: Tahap, Metrik kunci, Driver, Owner. */
export interface Stage {
  id: string;
  label: string;
  owner: string;
  /** What a team can move to change this stage's metrics (the doc's Driver column). */
  drivers: string[];
  /** The doc's "Metrik kunci" text, verbatim, so a reader can compare it with what was computed. */
  keyMetricsText: string;
  /** Metrics actually computed for this stage. May be fewer than the doc lists when the data lacks a column. */
  metricIds: string[];
}

/** A chart, already shaped for the kit component named by `kind`. */
export type Visual = { id: string; title: string; summary: string; stageId: string | null; format: MetricFormat } & (
  | { kind: "ranked"; categoryLabel: string; valueLabel: string; data: { label: string; value: number }[] }
  | { kind: "column"; categoryLabel: string; valueLabel: string; data: { label: string; value: number }[] }
  | { kind: "funnel"; valueLabel: string; data: { label: string; value: number }[] }
  | { kind: "part"; categoryLabel: string; valueLabel: string; data: { label: string; value: number }[] }
  | {
      kind: "trend";
      categoryLabel: string;
      series: { key: string; label: string }[];
      data: ({ label: string } & Record<string, number | string>)[];
    }
);

/** The three dashboard uses the doc gives per industry. */
export interface DashboardUses {
  diagnostic: string;
  operational: string;
  exploratory: string;
}

export interface Overview {
  industry: IndustrySummary;
  northStar: {
    label: string;
    /** The doc's "Rumus inti", verbatim. */
    formula: string;
    /** The computed North Star, when the data supports it. */
    metric: MetricValue | null;
    /** The formula's components, computed. */
    components: MetricValue[];
  };
  /** Two to four headline metrics for the stat row. */
  headline: MetricValue[];
  stages: Stage[];
  metrics: MetricValue[];
  visuals: Visual[];
  uses: DashboardUses;
}

/** One finding, graded. */
export interface Insight {
  id: string;
  title: string;
  verdict: Verdict;
  /** What the data says, in one or two sentences. */
  finding: string;
  metric: MetricValue;
  /** The rule behind the verdict, so a reader can check it. */
  basis: string;
  stageId: string;
  owner: string;
  /** A supporting chart, e.g. the metric by region, or null. */
  evidence: Visual | null;
  actionIds: string[];
}

export type ActionEffort = "low" | "medium" | "high";
export type ActionStatus = "open" | "doing" | "done";

export interface ActionItem {
  id: string;
  insightId: string;
  title: string;
  /** What to do, in plain sentences, built from the doc's drivers and the data. */
  detail: string;
  owner: string;
  /** The doc's drivers this action pulls. */
  drivers: string[];
  effort: ActionEffort;
  /** 1 is most urgent. */
  priority: number;
  /** The measurable it moves, stated as a target. */
  target: string;
  /** How many rows, customers, regions, or units it touches, with its unit. Null when not countable. */
  reach: { value: number; unit: string } | null;
  status: ActionStatus;
}

export type IssueSeverity = "high" | "medium" | "low";

export interface DataIssue {
  id: string;
  table: string;
  column: string | null;
  severity: IssueSeverity;
  title: string;
  detail: string;
  count: number;
}

export interface TableStatus {
  name: string;
  kind: "dimension" | "shared-fact" | "industry-fact";
  rows: number;
  columns: string[];
  /** 0 to 1: share of cells present. */
  completeness: number;
  /** The grain in words, from the doc, e.g. "1 aplikasi". */
  grain: string;
}

export interface DatasetStatus {
  industry: IndustrySlug;
  source: string;
  origin: "bundled" | "upload";
  loadedAt: string;
  tables: TableStatus[];
  issues: DataIssue[];
  completeness: number;
}

export interface IngestResult {
  ok: boolean;
  filename: string;
  /** Tables recognised, with their row counts. */
  tables: { name: string; rows: number }[];
  /** Tables the industry needs that the file lacks. Non-empty means ok is false. */
  missing: string[];
  /** Tables present that this industry does not use. */
  unknown: string[];
  errors: string[];
}

/** What a valid upload looks like for one tenant, for GET /api/i/:slug/dataset/schema. */
export interface DatasetSchema {
  industry: IndustrySlug;
  /** The upload route's own cap, in bytes. */
  maxUploadBytes: number;
  /** Accepted file kinds, e.g. [".json", ".json.gz", ".csv", ".xlsx"]. */
  accepts: string[];
  /** How a file maps to tables, in one or two plain sentences for the upload hint. */
  howTo: string;
  tables: {
    name: string;
    kind: "dimension" | "shared-fact" | "industry-fact";
    grain: string;
    columns: string[];
    /** False when the industry runs without it (a missing optional table is not an upload error). */
    required: boolean;
  }[];
}
